import React, { useState, useEffect, useRef } from "react";
import {
  Avatar,
  Box,
  Button,
  ColumnDatas,
  getColor,
  Icons,
  Table,
  Tooltip,
  Typography,
} from "fds";
import dayjs from "dayjs";
import "./index.scss";
import utilsCommon from "../../shared/utils/common";
import {
  meetingDTO,
  MeetingRequest,
  ZoomRequestDto,
} from "../../types/meeting";
import AvatarGroup from "fds/src/components/atom/Avatargroup";
import MeetingAside from "../MeetingAside";
import MeetingSettingButton from "../MeetingSettingButton";
import EmptyFilterPage from "../EmptyPage/EmptyFilterPage";
import { favoriteReq } from "../../types/files";
import classNames from "classnames";
import { getUserInfoFromLocalStorage } from "../../shared/utils/auth";
import { LoginDto } from "../../types/auth";
import useMeetingStore from "../../redux/dispatcher/useMeetingStore";
import { useTranslation } from "react-i18next";
import useWorkgroupStore from "../../redux/dispatcher/useWorkgroupStore";
interface MeetingBottomProps {
  filter?: favoriteReq | ZoomRequestDto | MeetingRequest;
  meetingList: meetingDTO[];
  changeFilterByKey?: (
    key: string,
    value: any,
    type?: "content" | "meeting"
  ) => void;
  handleCreateMeeting?: () => void;
  changeEditMode?: (meetingId: string) => void;
  height?: string | number;
  tableHeight?: string;
  isInitFilter?: boolean;
  classes?: string;
  page?:
    | "favorite"
    | "workgroup"
    | "client"
    | "client_fav"
    | "client_search"
    | "search";
  handleInitFilter?: () => void;
  handleRefreshMeetingList?: () => void;
}

const MeetingBottom: React.FC<MeetingBottomProps> = ({
  filter,
  meetingList,
  changeFilterByKey,
  height,
  tableHeight,
  isInitFilter,
  handleCreateMeeting,
  changeEditMode,
  handleInitFilter,
  handleRefreshMeetingList,
  page,
  classes,
}) => {
  const direction = document.documentElement.dir; // 'ltr' 또는 'rtl'
  const user = getUserInfoFromLocalStorage() as string | "";
  const loginDto: LoginDto = JSON.parse(user);
  const myId = loginDto?.userInfo?.userId ?? "";

  /** Hook 정리 */
  const { t } = useTranslation();
  const meetingListNew = useRef([] as any[]);
  const tableScrollRef = useRef<any>(null);
  const { setZoomMeetingFavorite } = useMeetingStore();
  const { setMeetingFavorite } = useWorkgroupStore();

  /** State 정리 */
  const [showDocAside, setShowDocAside] = useState<boolean>(false); //상세보기 on/off
  const [asideId, changeAsideId] = useState<string>("");
  const [key, setKey] = useState(0);
  const [meetingSettingInfo, setMeetingSettingInfo] = useState<{
    x: number;
    y: number;
    id: string;
    meetingInfo?: meetingDTO;
  }>({
    x: 0,
    y: 0,
    id: "",
    meetingInfo: undefined,
  });

  /** UseEffect 정리 */
  useEffect(() => {
    let newDataMeeting = [] as ColumnDatas[];
    meetingList?.forEach((item, index) => {
      newDataMeeting.push(getMeetingData(item, index));
    });
    meetingListNew.current = newDataMeeting;
    setKey(key + 1);
  }, [meetingList]);

  /** UI: 화상회의의 경우, 필터가 초기상태와 동일하며 검색결과가 없을때 나오는 화면 */
  const renderEmptyMeetingInitFilterPage = () => {
    return (
      <Box alignItems="center" direction="column">
        {page?.includes("search") ? (
          <img
            src={require("../../shared/images/pages/File/emptySearch.png")}
            style={{ marginTop: page?.includes("client") ? 100 : 160 }}
            alt="noFilter"
            width={200}
          />
        ) : page !== "workgroup" ? (
          <img
            src={require("../../shared/images/pages/Meeting/noMeeting.png")}
            style={{ marginTop: page?.includes("client") ? 100 : 160 }}
            alt="noFilter"
            width={200}
          />
        ) : (
          <img
            src={require("../../shared/images/pages/Meeting/detailThumbnail_lg.png")}
            style={{ marginTop: 160 }}
            alt="noFilter"
          />
        )}
        <Typography type="h3" classes="mg mt-20">
          {page?.includes("search")
            ? t("common.noFilterTitle")
            : t("common.noItemDisplay")}
        </Typography>
        <Typography
          type="body2_rg"
          classes="mg mt-8"
          textAlign="center"
          whiteSpace="break-spaces"
          exactColor={getColor("COLOR_TEXT_SECONDARY")}
        >
          {page === "workgroup"
            ? t("meeting.emptyList")
            : page?.includes("fav")
            ? t("bookmark.noMeeting")
            : t("search.noFile")}
        </Typography>
        {handleCreateMeeting && (
          <Button
            label={t("meeting.schedule")}
            btnStyle="fill"
            classes="mg mt-20"
            onClick={handleCreateMeeting}
            leadingIcon={<Icons variant="line" label="plusCircle" />}
          />
        )}
      </Box>
    );
  };
  /** UI: 화상회의 테이블 헤더 */
  const getProperMenu = () => {
    if (page === "client") {
      return [
        {
          headerText: "",
          width: "48px",
          order: "",
          orderAction: () => {},
        },
        {
          headerText: "",
          width: "293px",
          order: "",
          orderAction: () => {},
        },
        {
          headerText: "",
          width: "32px",
          order: "",
          orderAction: () => {},
        },
      ];
    } else if (page?.includes("client")) {
      return [
        {
          headerText: "",
          width: "50px",
          order: "",
          orderAction: () => {},
        },
        {
          headerText: "",
          width: direction === "ltr" ? "298px" : "285px",
          order: "",
          orderAction: () => {},
        },
        {
          headerText: "",
          width: "32px",
          order: "",
          orderAction: () => {},
        },
      ];
    } else {
      return [
        {
          headerText: "",
          width: "56px",
          order: "",
          orderAction: () => {},
        },
        {
          headerText: t("meeting.name"),
          width: "calc((100% - 516px) * 0.6)",
          order:
            filter && "order" in filter
              ? filter?.order === "name"
                ? "upOn"
                : filter?.order === "namedesc"
                ? "downOn"
                : "downOff"
              : undefined,
          orderAction: () =>
            meetingListNew.current.length > 0 &&
            changeFilterByKey &&
            changeFilterByKey("order", "name"),
        },
        {
          headerText: t("meeting.status"),
          width: "100px",
          order: "",
          orderAction: () =>
            meetingListNew.current.length > 0 &&
            changeFilterByKey &&
            changeFilterByKey("order", "date"),
        },
        {
          headerText: t("meeting.time"),
          width: "180px",
          order: "",
          orderAction: () => {},
        },
        {
          headerText: t("meeting.participants"),
          width: "calc((100% - 516px) * 0.4)",
          order: "",
          orderAction: () => {},
        },
        {
          headerText: t("file.lastUpdate"),
          width: "180px",
          order:
            filter && "order" in filter
              ? filter?.order === "date"
                ? "upOn"
                : filter?.order === "datedesc"
                ? "downOn"
                : "downOff"
              : undefined,
          orderAction: () =>
            meetingListNew.current.length > 0 &&
            changeFilterByKey &&
            changeFilterByKey("order", "date"),
        },
      ];
    }
  };
  /** UI: 화상회의 테이블 내용 */
  const getProperBody = (row: meetingDTO) => {
    const meetingStatus =
      dayjs(row.endTime).valueOf() > dayjs().valueOf() &&
      dayjs(row.startTime).valueOf() < dayjs().valueOf()
        ? "live"
        : dayjs(row.endTime).valueOf() > dayjs().valueOf()
        ? "waiting"
        : "finished";
    if (page?.includes("client")) {
      return [
        {
          columnText:
            page === "client_fav" ? (
              <Box
                classes="clientEco-groups"
                style={{ position: "absolute", insetInlineStart: "5px" }}
              >
                <Avatar
                  name={row.workgroupName}
                  uniqueCode={row.workgroupId}
                  shape="workgroup"
                />
                <img
                  src={
                    meetingStatus === "waiting"
                      ? require("../../shared/images/pages/Meeting/zoom_waiting.png")
                      : meetingStatus === "live"
                      ? require("../../shared/images/pages/Meeting/zoom_live.png")
                      : require("../../shared/images/pages/Meeting/zoom_finished.png")
                  }
                  width={32}
                  alt="zoom"
                  style={{ display: "flex" }}
                  // className="mg ml-8"
                />
              </Box>
            ) : (
              <img
                src={
                  meetingStatus === "waiting"
                    ? require("../../shared/images/pages/Meeting/zoom_waiting.png")
                    : meetingStatus === "live"
                    ? require("../../shared/images/pages/Meeting/zoom_live.png")
                    : require("../../shared/images/pages/Meeting/zoom_finished.png")
                }
                width={40}
                alt="zoom"
                className={direction === "ltr" ? "mg ml-8" : ""}
                style={{ display: "flex" }}
              />
            ),
        },
        {
          columnText: (
            <Box width={"100%"}>
              <Box
                direction={"column"}
                justifyContent="center"
                classes="mg ml-12"
                width={"calc(100% - 5px)"}
              >
                <Tooltip message={row.topic}>
                  <Typography type="body2_rg" classes="meeting-topic" ellipsis>
                    {row.topic}
                  </Typography>
                </Tooltip>
                <Typography
                  type="body4_rg"
                  classes="meeting-desc"
                  ellipsis
                  exactColor={getColor("COLOR_TEXT_SECONDARY")}
                >
                  {utilsCommon.renderDateFormat(row.startTime)} ~{" "}
                  {utilsCommon.renderDateFormat(row.endTime)}
                </Typography>
              </Box>
            </Box>
          ),
        },
        {
          columnText: (
            <Box
              classes={classNames("mg ml-auto meetingBottom-bookmark", {
                isOn: row.favorite === "Y",
              })}
              width={"fit-content"}
            >
              <Tooltip
                message={
                  row.favorite === "Y"
                    ? t("file.action.removeFav")
                    : t("file.action.addFav")
                }
              >
                <Button
                  btnStyle="table-icon"
                  onClick={() => handleSetFavZoom(row, row.favorite !== "Y")}
                  leadingIcon={
                    row.favorite === "Y" ? (
                      <Icons variant="fill" label="star" isColored size={20} />
                    ) : (
                      <Icons variant="line" label="star" isOn size={20} />
                    )
                  }
                />
              </Tooltip>
            </Box>
          ),
        },
      ];
    } else {
      return [
        {
          columnText: (
            <Box
              classes={classNames("mg ml-auto meetingBottom-bookmark", {
                isOn: row.favorite === "Y",
              })}
              width={"fit-content"}
              onClick={(e: any) => {
                e.stopPropagation();
                handleSetFavZoom(row, row.favorite !== "Y");
              }}
            >
              <Icons
                variant="fill"
                label="star"
                isColored={row.favorite === "Y"}
                size={24}
              />
            </Box>
          ),
        },
        {
          columnText: (
            <Box>
              <img
                src={
                  meetingStatus === "waiting"
                    ? require("../../shared/images/pages/Meeting/zoom_waiting.png")
                    : meetingStatus === "live"
                    ? require("../../shared/images/pages/Meeting/zoom_live.png")
                    : require("../../shared/images/pages/Meeting/zoom_finished.png")
                }
                width={40}
                alt="zoom"
              />
              <Box
                direction={"column"}
                justifyContent="center"
                classes="mg ml-12"
                width={"calc(100%  - 52px)"}
              >
                <Tooltip message={row.topic}>
                  <Typography type="subtitle2" classes="meeting-topic" ellipsis>
                    {row.topic}
                  </Typography>
                </Tooltip>
                <Tooltip message={row.workgroupName}>
                  <Typography
                    type="body4_rg"
                    classes="meeting-desc"
                    ellipsis
                    exactColor={getColor("COLOR_TEXT_SECONDARY")}
                  >
                    {row.workgroupName}
                  </Typography>
                </Tooltip>
              </Box>
            </Box>
          ),
        },
        {
          columnText: (
            <Typography type="body2_rg">
              {meetingStatus === "waiting"
                ? t("meeting.upcoming")
                : meetingStatus === "live"
                ? t("meeting.inProgress")
                : t("meeting.finish")}
            </Typography>
          ),
        },
        {
          columnText: (
            <Box direction="column">
              <Typography type="body2_rg">
                {utilsCommon.renderDateFormat(row.startTime)}
              </Typography>
              <Typography type="body2_rg">
                ~ {utilsCommon.renderDateFormat(row.endTime)}
              </Typography>
            </Box>
          ),
        },
        {
          columnText: (
            <Tooltip
              message={row.participantList
                ?.map((item) => item?.userName || item?.userId)
                .join(", ")}
            >
              <AvatarGroup
                visibleLength={8}
                size={"24"}
                items={utilsCommon.sortMembersToAvatarGroup(
                  row?.participantList,
                  row?.ownerId
                )}
              />
            </Tooltip>
          ),
        },
        {
          columnText: (
            <Typography type={"body2_rg"}>
              {utilsCommon.renderDateFormat(row.updateTime)}
            </Typography>
          ),
        },
      ];
    }
  };
  /** 웹으로 이동시 url 받으면 웹에서 열리도록 하는 함수 */
  const handleMoveZoomPage = (url: string) => {
    if (utilsCommon.isClientLogin("MAC")) {
      (window as any)?.webkit?.messageHandlers.webOpen.postMessage(url);
    } else {
      window.open(url);
    }
  };
  /** 화상회의 즐겨찾기 함수 */
  const handleSetFavZoom = (row: meetingDTO, flag: boolean) => {
    setZoomMeetingFavorite({
      meetingId: row.meetingId + row.occurrenceId,
      flag: flag,
      userId: myId,
      workgroupId: row.workgroupId,
      page: page,
    });
    setMeetingFavorite({
      meetingId: row.meetingId + row.occurrenceId,
      flag: flag,
      workgroupId: row.workgroupId,
    });
  };
  /** UI: 호버시 표시되는 action 들 */
  const renderMeetingHover = (meetingStatus: string, row: meetingDTO) => {
    return meetingStatus !== "finished" &&
      row?.participantListStr?.split(",")?.includes(myId) ? (
      page?.includes("client") ? (
        <>
          <Tooltip
            message={
              row.favorite === "Y"
                ? t("file.action.removeFav")
                : t("file.action.addFav")
            }
          >
            <Button
              btnStyle="table-icon"
              onClick={() => handleSetFavZoom(row, row.favorite !== "Y")}
              leadingIcon={
                row.favorite === "Y" ? (
                  <Icons variant="fill" label="star" isColored size={20} />
                ) : (
                  <Icons variant="line" label="star" isOn size={20} />
                )
              }
            />
          </Tooltip>
          {row.ownerId.toLowerCase() === myId ? (
            <Tooltip message={t("meeting.action.start")}>
              <Button
                btnStyle="table-icon"
                leadingIcon={<Icons variant="line" label="play" size={20} />}
                onClick={() => handleMoveZoomPage(row.startUrl)}
              />
            </Tooltip>
          ) : (
            <Tooltip message={t("meeting.action.join")}>
              <Button
                btnStyle="table-icon"
                leadingIcon={<Icons variant="line" label="zoom" size={20} />}
                onClick={() => handleMoveZoomPage(row.joinUrl)}
              />
            </Tooltip>
          )}
        </>
      ) : (
        <Button
          btnStyle={"icon"}
          size="sm"
          classes="mg ml-4"
          onClick={(e) => {
            e.stopPropagation();
            meetingStatus !== "finished" &&
              setMeetingSettingInfo({
                x: e.pageX - 240,
                y: e.pageY - 61,
                id:
                  row.workgroupId +
                  "@@" +
                  row.meetingId +
                  "@@" +
                  row.occurrenceId,
                meetingInfo: row,
              });
          }}
          leadingIcon={<Icons variant="line" label="moreVertical" />}
        />
      )
    ) : undefined;
  };
  /** UI: 화상회의 row */
  const getMeetingData = (row: meetingDTO, index: number): ColumnDatas => {
    const meetingStatus =
      dayjs(row.endTime).valueOf() > dayjs().valueOf() &&
      dayjs(row.startTime).valueOf() < dayjs().valueOf()
        ? "live"
        : dayjs(row.endTime).valueOf() > dayjs().valueOf()
        ? "waiting"
        : "finished";
    return {
      clickAction: () => {
        if (page?.includes("client")) return;
        changeAsideId(
          row.workgroupId + "@@" + row.meetingId + "@@" + row.occurrenceId
        );
        setShowDocAside(true);
      },
      clickRAction: (e) => {
        e.stopPropagation();
        if (page?.includes("client")) return;
        meetingStatus !== "finished" &&
          row?.participantListStr?.split(",")?.includes(myId) &&
          setMeetingSettingInfo({
            x: e.pageX - 240,
            y: e.pageY - 61,
            id:
              row.workgroupId + "@@" + row.meetingId + "@@" + row.occurrenceId,
            meetingInfo: row,
          });
      },
      hover: renderMeetingHover(meetingStatus, row),
      data: getProperBody(row),
      id: row.workgroupId + "@@" + row.meetingId + "@@" + row.occurrenceId,
    };
  };
  return (
    <Box
      height={height}
      classes={classNames("meetingBottom", {
        client: page?.includes("client"),
      })}
    >
      {meetingSettingInfo.id && (
        <MeetingSettingButton
          meetingSettingInfo={meetingSettingInfo}
          setMeetingSettingInfo={setMeetingSettingInfo}
          changeEditMode={changeEditMode}
          handleRefreshMeetingList={handleRefreshMeetingList}
        />
      )}
      <Box
        width="100%"
        direction="column"
        alignItems="center"
        style={{ paddingBottom: "5px" }}
      >
        <Table
          width={page?.includes("client") ? "392px" : "100%"}
          header={getProperMenu()}
          asideId={page?.includes("client") ? undefined : asideId}
          asideColor={"theme"}
          columnHeight={page?.includes("client") ? "md" : "lg"}
          height={
            meetingListNew.current.length === 0
              ? undefined
              : tableHeight ?? "calc(100vh - 353px)"
          }
          style={{ paddingBottom: "1px" }}
          endReached={() => {
            meetingListNew.current.length >= 20 &&
              changeFilterByKey &&
              changeFilterByKey(
                filter && "pageNo" in filter ? "pageNo" : "page",
                filter && "pageNo" in filter
                  ? filter?.pageNo + 1
                  : filter && "page" in filter
                  ? { content: 0, meeting: filter?.page.meeting + 1 }
                  : 0,
                "meeting"
              );
          }}
          showHeader
          data={meetingListNew.current}
          totalCount={meetingListNew.current.length}
          sideMenu={
            page?.includes("client") ? undefined : showDocAside ? "off" : "on"
          }
          border={"boundary"}
          tableScrollRef={tableScrollRef}
          sideMenuAction={() => setShowDocAside(!showDocAside)}
        />
        {meetingListNew.current.length === 0 &&
          (isInitFilter ? (
            renderEmptyMeetingInitFilterPage()
          ) : (
            <EmptyFilterPage
              handleInitFilter={handleInitFilter}
              pageType={page}
            />
          ))}
      </Box>
      {showDocAside && <MeetingAside asideId={asideId} />}
    </Box>
  );
};
export default MeetingBottom;
